import type { ImportedPlayerResult } from "./types";

export interface ParsedStandingsReport {
  eventName: string;
  eventDate: string;
  results: ImportedPlayerResult[];
}

const percentage = String.raw`(\d+(?:\.\d+)?)%?`;
const record = String.raw`(\d+)\s*-\s*(\d+)\s*-\s*(\d+)`;
const score = String.raw`(\d+)\s*(?:Pts|Points)?`;
const status = String.raw`(?:\s+(Active|Dropped))?`;
/** Riot tagline shown under the handle, e.g. `#NA1`, `#Grp A`. */
const tagline = /^#\S/;
const statusLine = /^\(?(Active|Dropped)\)?$/i;

/**
 * Row from the new Riftbound tournament platform, copied as a single
 * (tab/space separated) line:
 *   `5  Maverik  2-1-0  6 Pts  55.6%  66.7%  55.6%  Active`
 */
const platformRow = new RegExp(
  `^(\\d+)\\s+(.+?)\\s+${record}\\s+${score}\\s+${percentage}\\s+${percentage}\\s+${percentage}${status}$`,
  "i"
);

/**
 * Same platform, but copied with one field per line:
 *   5 / Maverik / 2-1-0 / 6 Pts / 55.6% / 66.7% / 55.6% / Active
 * Newer exports add a tagline line under the name and put a dropped marker
 * before the record instead of a status after the percentages:
 *   15 / xPrimary / #NA1 / (Dropped) / 1 - 2 - 0 / 3 Pts / 69.4% / 33.0% / 66.7%
 * Returns the parsed result and how many lines were consumed, or null.
 */
function parsePlatformBlock(
  lines: string[],
  start: number
): { result: ImportedPlayerResult; consumed: number } | null {
  const rankLine = lines[start];
  const nameLine = lines[start + 1];
  if (!/^\d+$/.test(rankLine || "") || !nameLine) return null;

  let cursor = start + 2;
  if (tagline.test(lines[cursor] || "")) cursor++;
  if (statusLine.test(lines[cursor] || "")) cursor++;

  const [recordLine, scoreLine, omwLine, gwLine, ogwLine] = lines.slice(
    cursor,
    cursor + 5
  );
  if (!ogwLine) return null;

  const recordMatch = recordLine.match(new RegExp(`^${record}$`));
  const scoreMatch = scoreLine.match(new RegExp(`^${score}$`, "i"));
  const pct = new RegExp(`^${percentage}$`);
  const omwMatch = omwLine.match(pct);
  const gwMatch = gwLine.match(pct);
  const ogwMatch = ogwLine.match(pct);

  if (!recordMatch || !scoreMatch || !omwMatch || !gwMatch || !ogwMatch) {
    return null;
  }

  cursor += 5;
  if (statusLine.test(lines[cursor] || "")) cursor++;

  return {
    result: {
      rank: Number(rankLine),
      name: nameLine.trim(),
      wins: Number(recordMatch[1]),
      losses: Number(recordMatch[2]),
      draws: Number(recordMatch[3]),
      points: Number(scoreMatch[1]),
      omw: Number(omwMatch[1]),
      gw: Number(gwMatch[1]),
      ogw: Number(ogwMatch[1]),
    },
    consumed: cursor - start,
  };
}

/**
 * Extract the in-game handle from a player name. EventLink exports names as
 * `Last, First (Handle)`; the new Riftbound platform exports just `Handle`.
 */
export function getPlayerHandle(name: string): string | null {
  const match = name.match(/\(([^()]+)\)\s*$/);
  return match ? match[1].trim() : null;
}

const normalize = (value: string) =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

/**
 * Find the existing player construct that an imported result name refers to.
 * Matches, in priority order:
 *   1. exact name (case-insensitive)
 *   2. imported handle vs. construct handle (`Maverik` vs `Dodds, Matt (Maverik)`)
 *   3. imported full name vs. construct handle, or vice versa
 * When several constructs match, prefer one already linked to a user account.
 */
export function findMatchingConstruct<
  T extends { name: string; userId?: string | null }
>(importedName: string, constructs: T[]): T | undefined {
  const target = normalize(importedName);
  const targetHandle = getPlayerHandle(importedName);
  const targetKey = normalize(targetHandle || importedName);

  const exact = constructs.filter((c) => normalize(c.name) === target);
  if (exact.length > 0) return preferLinked(exact);

  const byHandle = constructs.filter((c) => {
    const handle = getPlayerHandle(c.name);
    const constructKey = normalize(handle || c.name);
    return constructKey === targetKey;
  });
  return byHandle.length > 0 ? preferLinked(byHandle) : undefined;
}

const preferLinked = <T extends { userId?: string | null }>(items: T[]): T =>
  items.find((item) => item.userId) || items[0];

export function parseStandingsReport(
  data: string,
  fallbackDate = new Date().toISOString().split("T")[0]
): ParsedStandingsReport | null {
  const lines = data
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const eventName =
    lines.find((line) => /^Standings\s*-/i.test(line))?.replace(
      /^Standings\s*-\s*/i,
      ""
    ) ||
    lines.find((line) => /^Event:/i.test(line))?.replace(/^Event:\s*/i, "") ||
    "Imported Event";
  const eventDate =
    lines
      .find((line) => /^Event Date:/i.test(line))
      ?.replace(/^Event Date:\s*/i, "") || fallbackDate;
  const roundCount = Math.max(
    0,
    ...lines.map(
      (line) => Number(line.match(/^Round\s+(\d+)/i)?.[1] || 0)
    )
  );

  const riftboundRow = new RegExp(
    `^(\\d+)\\s+(.+?)\\s+(\\d+)\\s+(\\d+)-(\\d+)-(\\d+)\\s+${percentage}\\s+${percentage}\\s+${percentage}$`
  );
  const legacyRow = new RegExp(
    `^(\\d+)\\s+(.+?)\\s+(\\d+)\\s+${percentage}\\s+${percentage}\\s+${percentage}$`
  );
  const results: ImportedPlayerResult[] = [];

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];

    const block = parsePlatformBlock(lines, index);
    if (block) {
      results.push(block.result);
      index += block.consumed - 1;
      continue;
    }

    const platformMatch = line.match(platformRow);
    if (platformMatch) {
      results.push({
        rank: Number(platformMatch[1]),
        name: platformMatch[2].trim(),
        wins: Number(platformMatch[3]),
        losses: Number(platformMatch[4]),
        draws: Number(platformMatch[5]),
        points: Number(platformMatch[6]),
        omw: Number(platformMatch[7]),
        gw: Number(platformMatch[8]),
        ogw: Number(platformMatch[9]),
      });
      continue;
    }

    const riftboundMatch = line.match(riftboundRow);
    if (riftboundMatch) {
      results.push({
        rank: Number(riftboundMatch[1]),
        name: riftboundMatch[2].trim(),
        points: Number(riftboundMatch[3]),
        wins: Number(riftboundMatch[4]),
        losses: Number(riftboundMatch[5]),
        draws: Number(riftboundMatch[6]),
        omw: Number(riftboundMatch[7]),
        gw: Number(riftboundMatch[8]),
        ogw: Number(riftboundMatch[9]),
      });
      continue;
    }

    const legacyMatch = line.match(legacyRow);
    if (legacyMatch) {
      const points = Number(legacyMatch[3]);
      const wins = Math.floor(points / 3);
      const draws = points % 3;
      results.push({
        rank: Number(legacyMatch[1]),
        name: legacyMatch[2].trim(),
        points,
        wins,
        losses: Math.max(0, (roundCount || 5) - wins - draws),
        draws,
        omw: Number(legacyMatch[4]),
        gw: Number(legacyMatch[5]),
        ogw: Number(legacyMatch[6]),
      });
    }
  }

  return results.length > 0 ? { eventName, eventDate, results } : null;
}
